/**
 * Users Admin API Client
 *
 * API client for user administration (admin-only).
 */

import { httpClient } from "./client";
import type { IUser, IUpdateUserRequest, IResetPasswordRequest, ICreateUserRequest } from "../types/auth";
import {
  assertObject,
  assertEntityId,
  assertString,
  assertBoolean,
  assertArrayOf,
} from "../types/validation";

// ============================================================================
// Runtime Validators
// ============================================================================

/** Backend uses skip_serializing_if for Option — field may be absent (undefined) */
function assertOptionalString(value: unknown, path: string): string | null {
  if (value === null || value === undefined) return null;
  return assertString(value, path);
}

export function validateUser(data: unknown, path: string): IUser {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    login: assertString(obj['login'], `${path}.login`),
    email: assertString(obj['email'], `${path}.email`),
    fullName: assertString(obj['fullName'], `${path}.fullName`),
    isActive: assertBoolean(obj['isActive'], `${path}.isActive`),
    isAdmin: assertBoolean(obj['isAdmin'], `${path}.isAdmin`),
    mustChangePassword: assertBoolean(obj['mustChangePassword'], `${path}.mustChangePassword`),
    lastPasswordChange: assertOptionalString(obj['lastPasswordChange'], `${path}.lastPasswordChange`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
  };
}

// ============================================================================
// API Client
// ============================================================================

export interface IUsersAdminApiClient {
  listUsers(): Promise<readonly IUser[]>;
  getUser(userId: string): Promise<IUser>;
  updateUser(userId: string, data: IUpdateUserRequest): Promise<IUser>;
  resetPassword(userId: string, data: IResetPasswordRequest): Promise<void>;
  createUser(data: ICreateUserRequest): Promise<IUser>;
}

class UsersAdminApiClient implements IUsersAdminApiClient {
  async listUsers(): Promise<readonly IUser[]> {
    const response = await httpClient.get<unknown>("/admin/users");
    return assertArrayOf(response.data, validateUser, "listUsers");
  }

  async getUser(userId: string): Promise<IUser> {
    const response = await httpClient.get<unknown>(`/admin/users/${userId}`);
    return validateUser(response.data, "getUser");
  }

  async updateUser(userId: string, data: IUpdateUserRequest): Promise<IUser> {
    const response = await httpClient.put<unknown>(`/admin/users/${userId}`, data);
    return validateUser(response.data, "updateUser");
  }

  async resetPassword(userId: string, data: IResetPasswordRequest): Promise<void> {
    await httpClient.post(`/admin/users/${userId}/reset-password`, data);
  }

  async createUser(data: ICreateUserRequest): Promise<IUser> {
    const response = await httpClient.post<unknown>("/admin/users", data);
    return validateUser(response.data, "createUser");
  }
}

// Singleton instance
let usersAdminApiClient: UsersAdminApiClient | null = null;

export function getUsersAdminApiClient(): UsersAdminApiClient {
  usersAdminApiClient ??= new UsersAdminApiClient();
  return usersAdminApiClient;
}
